<template>
  <b-modal :title="helper.trans('labels.plan_settings')"
           :modelValue="show"
           @update:modelValue="show = false"
           size="lg">
    
    <LoaderWrapper v-if="loading" :zoom="0.5">
      <div class="text-center">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
    </LoaderWrapper>

    <div v-else>
      <div class="row g-3 mb-3">
        <div class="col-12">
          <div class="d-flex gap-2">
            <button type="button" @click="disablePlanForAll" class="btn btn-outline-danger btn-sm">
              {{ helper.trans('plan_settings.disable_plan_for_all_traders') }}
            </button>
          </div>
        </div>
      </div>

      <div v-if="settings && settings.length > 0" class="mb-4">
        <h6>{{ helper.trans('plan_settings.current_settings') }}</h6>
        <div v-for="(setting, index) in settings" :key="setting.id || index" class="card mb-3">
          <div class="card-body">
            <div class="row g-3 mb-3">
              <div class="col-12">
                <label :for="`planType-${setting.id}`" class="form-label">{{ helper.trans('plan_settings.plan_type') }}</label>
                <select :id="`planType-${setting.id}`" class="form-select" v-model="setting.planType">
                  <option :value="PlanType.MANDATORY">{{ helper.trans('plan_settings.plan_type_mandatory') }}</option>
                  <option :value="PlanType.OPTIONAL">{{ helper.trans('plan_settings.plan_type_optional') }}</option>
                </select>
              </div>
            </div>
            <div class="row g-3 mb-3">
              <div class="col-12">
                <label :for="`rewardType-${setting.id}`" class="form-label">{{ helper.trans('plan_settings.reward_type') }}</label>
                <select :id="`rewardType-${setting.id}`" class="form-select" v-model="setting.rewardType" @change="onCardRewardTypeChange(setting)">
                  <option :value="PlanRewardType.UNLOCK_REQUISITES">{{ helper.trans('plan_settings.reward_type_unlock_requisites') }}</option>
                  <option :value="PlanRewardType.PRIORITY_BOOST">{{ helper.trans('plan_settings.reward_type_priority_boost') }}</option>
                </select>
              </div>
            </div>
            <div class="row g-3 mb-3">
              <div class="col-12">
                <label :for="`name-${setting.id}`" class="form-label">{{ helper.trans('plan_settings.name') }}</label>
                <input type="text" class="form-control" :id="`name-${setting.id}`" v-model="setting.name" :placeholder="helper.trans('plan_settings.name_placeholder')">
              </div>
            </div>
            <div class="row g-3 mb-3">
              <div class="col-12">
                <label :for="`currency-${setting.id}`" class="form-label">{{ helper.trans('labels.currency') }}</label>
                <select class="form-select" :id="`currency-${setting.id}`" v-model="setting.currency">
                  <option value="">{{ helper.trans('plan_settings.select_currency') }}</option>
                  <option v-for="currency in currencyList" :key="currency" :value="currency">{{ currency }}</option>
                </select>
              </div>
            </div>
            <div class="row g-3 mb-3">
              <div class="col-12">
                <label :for="`planTimer-${setting.id}`" class="form-label">{{ helper.trans('plan_settings.timer') }} ({{ helper.trans('plan_settings.hours_minutes_seconds') }})</label>
                <input type="time" class="form-control" :id="`planTimer-${setting.id}`" v-model="setting.planTimerDisplay" step="1">
              </div>
            </div>
            <div class="row g-3 mb-3">
              <div class="col-12">
                <label :for="`planCompletedDeal-${setting.id}`" class="form-label">{{ helper.trans('plan_settings.completed_deals') }}</label>
                <input type="number" class="form-control" :id="`planCompletedDeal-${setting.id}`" v-model="setting.planCompletedDeal" placeholder="0">
              </div>
            </div>
            <div v-if="getDealAmountRangeText(setting)" class="row g-3 mb-2">
              <div class="col-12">
                <small class="text-muted">{{ getDealAmountRangeText(setting) }}</small>
              </div>
            </div>
            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <label :for="`planDealAmountMin-${setting.id}`" class="form-label">{{ helper.trans('plan_settings.plan_deal_amount_min') }}</label>
                <input type="text" class="form-control" :id="`planDealAmountMin-${setting.id}`" v-model="setting.planDealAmountMinDisplay" :placeholder="helper.trans('plan_settings.plan_deal_amount_min')">
              </div>
              <div class="col-md-6">
                <label :for="`planDealAmountMax-${setting.id}`" class="form-label">{{ helper.trans('plan_settings.plan_deal_amount_max') }}</label>
                <input type="text" class="form-control" :id="`planDealAmountMax-${setting.id}`" v-model="setting.planDealAmountMaxDisplay" :placeholder="helper.trans('plan_settings.plan_deal_amount_max')">
              </div>
            </div>
            <div class="row g-3 mb-3">
              <div class="col-12">
                <label :for="`allowedPaymentOptions-${setting.id}`" class="form-label">{{ helper.trans('plan_settings.allowed_payment_options') }}</label>
                <Multiselect :id="`allowedPaymentOptions-${setting.id}`"
                             class=""
                             v-model="setting.allowedPaymentOptions"
                             :options="paymentOptionsList(setting.currency)"
                             :custom-label="(opt: string) => helper.trans(`payment_options.${opt}`)"
                             :multiple="true"
                             select-label="Select"
                             :placeholder="helper.trans('plan_settings.select_payment_methods')"
                >
                  <template #option="props">
                    <div class="d-flex flex-row align-items-center gap-2">
                      <span>{{ helper.trans('payment_options.' + props.option) }}</span>
                    </div>
                  </template>
                </Multiselect>
              </div>
            </div>

            <div v-if="setting.rewardType !== PlanRewardType.PRIORITY_BOOST" class="row g-3 mb-3">
              <div class="col-12">
                <label :for="`planRequisiteLimit-${setting.id}`" class="form-label">{{ helper.trans('plan_settings.plan_requisite_limit') }}</label>
                <input type="text" class="form-control" :id="`planRequisiteLimit-${setting.id}`" v-model="setting.planRequisiteLimitDisplay" placeholder="0.00">
              </div>
            </div>
            <div v-if="setting.rewardType === PlanRewardType.PRIORITY_BOOST" class="row g-3 mb-3">
              <div class="col-12">
                <label :for="`rewardPriority-${setting.id}`" class="form-label">{{ helper.trans('plan_settings.reward_priority') }}</label>
                <select :id="`rewardPriority-${setting.id}`" class="form-select" v-model="setting.rewardPriority">
                  <option :value="null">{{ helper.trans('plan_settings.not_set') }}</option>
                  <option :value="PlanRewardPriority.HIGH">{{ helper.trans('plan_settings.reward_priority_high') }}</option>
                  <option :value="PlanRewardPriority.MEDIUM">{{ helper.trans('plan_settings.reward_priority_medium') }}</option>
                  <option :value="PlanRewardPriority.LOW">{{ helper.trans('plan_settings.reward_priority_low') }}</option>
                </select>
              </div>
            </div>

            <div class="d-flex flex-column gap-2">
              <div class="d-flex gap-2">
                <button type="button" @click="enablePlanForSetting(setting.id)" class="btn btn-outline-secondary btn-sm">
                  {{ helper.trans('plan_settings.enable_plan_for_setting') }}
                </button>
                <button type="button" @click="disablePlanForSetting(setting.id)" class="btn btn-outline-secondary btn-sm">
                  {{ helper.trans('plan_settings.disable_plan_for_setting') }}
                </button>
              </div>
              <div class="d-flex gap-2">
                <button type="button" @click="updateSetting(setting)" class="btn btn-outline-primary btn-sm">
                  {{ helper.trans('button.save') }}
                </button>
                <button type="button" @click="deleteSetting(setting)" class="btn btn-outline-danger btn-sm">
                  {{ helper.trans('button.delete') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!editingSetting" class="row g-3 mb-3">
        <div class="col-12">
          <button type="button" @click="addNewSetting" class="btn btn-outline-success btn-sm">
            {{ helper.trans('button.add_new_setting') }}
          </button>
        </div>
      </div>

      <div v-if="editingSetting" class="card">
        <div class="card-header">
          <h6 class="mb-0">{{ editingSetting.id ? helper.trans('button.edit_setting') : helper.trans('button.add_new_setting') }}</h6>
        </div>
        <div class="card-body">
          <div class="row g-3 mb-3">
            <div class="col-12">
              <label for="planType-editing" class="form-label">{{ helper.trans('plan_settings.plan_type') }}</label>
              <select id="planType-editing" class="form-select" v-model="editingSetting.planType">
                <option :value="PlanType.MANDATORY">{{ helper.trans('plan_settings.plan_type_mandatory') }}</option>
                <option :value="PlanType.OPTIONAL">{{ helper.trans('plan_settings.plan_type_optional') }}</option>
              </select>
            </div>
          </div>
          <div class="row g-3 mb-3">
            <div class="col-12">
              <label for="rewardType-editing" class="form-label">{{ helper.trans('plan_settings.reward_type') }}</label>
              <select id="rewardType-editing" class="form-select" v-model="editingSetting.rewardType" @change="onEditingRewardTypeChange()">
                <option :value="PlanRewardType.UNLOCK_REQUISITES">{{ helper.trans('plan_settings.reward_type_unlock_requisites') }}</option>
                <option :value="PlanRewardType.PRIORITY_BOOST">{{ helper.trans('plan_settings.reward_type_priority_boost') }}</option>
              </select>
            </div>
          </div>
          <div class="row g-3 mb-3">
            <div class="col-12">
              <label for="name" class="form-label">{{ helper.trans('plan_settings.name') }}</label>
              <input type="text" class="form-control" id="name" v-model="editingSetting.name" :placeholder="helper.trans('plan_settings.name_placeholder')">
            </div>
          </div>
          <div class="row g-3 mb-3">
            <div class="col-12">
              <label for="currency" class="form-label">{{ helper.trans('labels.currency') }}</label>
              <select class="form-select" id="currency" v-model="editingSetting.currency">
                <option value="">{{ helper.trans('plan_settings.select_currency') }}</option>
                <option v-for="currency in currencyList" :key="currency" :value="currency">{{ currency }}</option>
              </select>
            </div>
          </div>
          <div class="row g-3 mb-3">
            <div class="col-12">
              <label for="planTimer" class="form-label">{{ helper.trans('plan_settings.timer') }} ({{ helper.trans('plan_settings.hours_minutes_seconds') }})</label>
              <input type="time" class="form-control" id="planTimer" v-model="editingSetting.planTimerDisplay" step="1">
            </div>
          </div>
          <div class="row g-3 mb-3">
            <div class="col-12">
              <label for="planCompletedDeal" class="form-label">{{ helper.trans('plan_settings.completed_deals') }}</label>
              <input type="number" class="form-control" id="planCompletedDeal" v-model="editingSetting.planCompletedDeal" placeholder="0">
            </div>
          </div>
          <div class="row g-3 mb-3">
            <div class="col-md-6">
              <label for="planDealAmountMin-editing" class="form-label">{{ helper.trans('plan_settings.plan_deal_amount_min') }}</label>
              <input type="text" class="form-control" id="planDealAmountMin-editing" v-model="editingSetting.planDealAmountMinDisplay" :placeholder="helper.trans('plan_settings.plan_deal_amount_min')">
            </div>
            <div class="col-md-6">
              <label for="planDealAmountMax-editing" class="form-label">{{ helper.trans('plan_settings.plan_deal_amount_max') }}</label>
              <input type="text" class="form-control" id="planDealAmountMax-editing" v-model="editingSetting.planDealAmountMaxDisplay" :placeholder="helper.trans('plan_settings.plan_deal_amount_max')">
            </div>
          </div>
          <div class="row g-3 mb-3">
            <div class="col-12">
              <label for="allowedPaymentOptions-editing" class="form-label">{{ helper.trans('plan_settings.allowed_payment_options') }}</label>
              <Multiselect id="allowedPaymentOptions-editing"
                           class=""
                           v-model="editingSetting.allowedPaymentOptions"
                           :options="paymentOptionsList(editingSetting.currency)"
                           :custom-label="(opt: string) => helper.trans(`payment_options.${opt}`)"
                           :multiple="true"
                           select-label="Select"
                           :placeholder="helper.trans('plan_settings.select_payment_methods')"
              >
                <template #option="props">
                  <div class="d-flex flex-row align-items-center gap-2">
                    <span>{{ helper.trans('payment_options.' + props.option) }}</span>
                  </div>
                </template>
              </Multiselect>
            </div>
          </div>

          <div v-if="editingSetting.rewardType !== PlanRewardType.PRIORITY_BOOST" class="row g-3 mb-3">
            <div class="col-12">
              <label for="planRequisiteLimit" class="form-label">{{ helper.trans('plan_settings.plan_requisite_limit') }}</label>
              <input type="text" class="form-control" id="planRequisiteLimit" v-model="editingSetting.planRequisiteLimitDisplay" placeholder="0.00">
            </div>
          </div>
          <div v-if="editingSetting.rewardType === PlanRewardType.PRIORITY_BOOST" class="row g-3 mb-3">
            <div class="col-12">
              <label for="rewardPriority-editing" class="form-label">{{ helper.trans('plan_settings.reward_priority') }}</label>
              <select id="rewardPriority-editing" class="form-select" v-model="editingSetting.rewardPriority">
                <option :value="null">{{ helper.trans('plan_settings.not_set') }}</option>
                <option :value="PlanRewardPriority.HIGH">{{ helper.trans('plan_settings.reward_priority_high') }}</option>
                <option :value="PlanRewardPriority.MEDIUM">{{ helper.trans('plan_settings.reward_priority_medium') }}</option>
                <option :value="PlanRewardPriority.LOW">{{ helper.trans('plan_settings.reward_priority_low') }}</option>
              </select>
            </div>
          </div>

          <div class="d-flex gap-2">
            <button type="button" @click="saveSetting" class="btn btn-outline-primary btn-sm">
              {{ helper.trans('button.save') }}
            </button>
            <button type="button" @click="cancelEditing" class="btn btn-outline-secondary btn-sm">
              {{ helper.trans('button.cancel') }}
            </button>
          </div>
        </div>
      </div>
    </div>

  </b-modal>
</template>

<script lang="ts">
import {defineComponent, reactive, ref, computed, watch} from 'vue'
import {BModal} from "bootstrap-vue-3";
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import {api} from "@/service/APIClient";
import {useStore} from "vuex";
import TraderRequisitesSettingDTO from "@/models/TraderRequisitesSettingDTO";
import TraderRequisitesSettingCreateRequestDTO from "@/models/request/TraderRequisitesSettingCreateRequestDTO";
import TraderRequisitesSettingUpdateRequestDTO from "@/models/request/TraderRequisitesSettingUpdateRequestDTO";
import TraderRequisitesSettingToggleRequestDTO from "@/models/request/TraderRequisitesSettingToggleRequestDTO";
import { PlanType } from "@/models/enum/PlanType";
import { PlanRewardType } from "@/models/enum/PlanRewardType";
import { PlanRewardPriority } from "@/models/enum/PlanRewardPriority";
import {helper} from "@/service/CommonHelper";
import {useNotification} from "@kyvg/vue3-notification";
import Multiselect from 'vue-multiselect';

interface EditingSetting extends TraderRequisitesSettingDTO {
  planTimerDisplay: string;
  planRequisiteLimitDisplay: string;
  planDealAmountMinDisplay: string;
  planDealAmountMaxDisplay: string;
  allowedPaymentOptions: string[] | null;
  name: string;
}

interface SettingWithTimerUnit extends TraderRequisitesSettingDTO {
  planTimerDisplay: string;
  planRequisiteLimitDisplay: string;
  planDealAmountMinDisplay: string;
  planDealAmountMaxDisplay: string;
  allowedPaymentOptions: string[] | null;
  name: string;
}

export default defineComponent({
  components: {
    BModal,
    LoaderWrapper,
    Multiselect
  },
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const store = useStore()
    const notification = useNotification()
    const loading = ref(false)
    const settings = ref<SettingWithTimerUnit[]>([])
    const show = ref(false)
    const editingSetting = ref<EditingSetting | null>(null)
    const currencyList = computed(() => store.getters["currencyMethods/currencies"])
    const paymentOptionsList = computed(() => store.getters["currencyMethods/options"])

    const form = reactive({
      enabled: false
    })

    const convertSecondsToTime = (seconds: number | null): string => {
      if (!seconds) return '00:00:00'
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      const secs = seconds % 60
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }

    const convertTimeToSeconds = (timeString: string): number => {
      const [hours, minutes, seconds] = timeString.split(':').map(Number)
      return (hours * 3600) + (minutes * 60) + seconds
    }

    const loadSettings = async () => {
      try {
        loading.value = true
        const response = await api.traderRequisitesSettings()
        settings.value = response.map(setting => {
          const rewardType = setting.rewardType ?? PlanRewardType.UNLOCK_REQUISITES
          const isPriorityBoost = rewardType === PlanRewardType.PRIORITY_BOOST
          const rewardPriority = isPriorityBoost && (setting.rewardPriority == null || setting.rewardPriority === undefined)
            ? PlanRewardPriority.MEDIUM
            : (setting.rewardPriority ?? null)
          return {
            ...setting,
            planType: setting.planType ?? PlanType.MANDATORY,
            rewardType,
            rewardPriority,
            planTimerDisplay: convertSecondsToTime(setting.planTimer),
            planRequisiteLimitDisplay: isPriorityBoost ? '0.00' : (setting.planRequisiteLimit || '0.00'),
            planDealAmountMinDisplay: setting.planDealAmountMin ?? '',
            planDealAmountMaxDisplay: setting.planDealAmountMax ?? '',
            allowedPaymentOptions: setting.allowedPaymentOptions || null
          }
        })
      } catch (error) {
        notification.notify({
          type: 'error',
          text: helper.trans('server_errors.INTERNAL_ERROR')
        })
      } finally {
        loading.value = false
      }
    }

    const addNewSetting = () => {
      editingSetting.value = {
        id: 0,
        name: '',
        currency: '',
        planRequisiteLimit: '0.00',
        planDealAmountMin: null,
        planDealAmountMax: null,
        planTimer: 0,
        planCompletedDeal: 0,
        planTimerDisplay: '00:00:00',
        planRequisiteLimitDisplay: '0.00',
        planDealAmountMinDisplay: '',
        planDealAmountMaxDisplay: '',
        allowedPaymentOptions: null,
        planType: PlanType.MANDATORY,
        rewardType: PlanRewardType.UNLOCK_REQUISITES,
        rewardPriority: null
      }
    }

    const editSetting = (setting: TraderRequisitesSettingDTO) => {
      const rewardType = setting.rewardType ?? PlanRewardType.UNLOCK_REQUISITES
      const isPriorityBoost = rewardType === PlanRewardType.PRIORITY_BOOST
      const rewardPriority = isPriorityBoost && (setting.rewardPriority == null || setting.rewardPriority === undefined)
        ? PlanRewardPriority.MEDIUM
        : (setting.rewardPriority ?? null)
      editingSetting.value = {
        ...setting,
        planType: setting.planType ?? PlanType.MANDATORY,
        rewardType,
        rewardPriority,
        planTimerDisplay: convertSecondsToTime(setting.planTimer),
        planRequisiteLimitDisplay: isPriorityBoost ? '0.00' : (setting.planRequisiteLimit || '0.00'),
        planDealAmountMinDisplay: setting.planDealAmountMin ?? '',
        planDealAmountMaxDisplay: setting.planDealAmountMax ?? '',
        allowedPaymentOptions: setting.allowedPaymentOptions || null
      }
    }

    const normalizeDealAmount = (v: string | undefined): string => {
      const s = (v ?? '').trim().replace(',', '.')
      return s
    }

    const getDealAmountRangeText = (setting: { planDealAmountMin?: string | null; planDealAmountMax?: string | null; planDealAmountMinDisplay?: string; planDealAmountMaxDisplay?: string }): string | null => {
      const min = (setting.planDealAmountMinDisplay ?? setting.planDealAmountMin ?? '').trim() || null
      const max = (setting.planDealAmountMaxDisplay ?? setting.planDealAmountMax ?? '').trim() || null
      if (min && max) return helper.trans('plan_settings.plan_deal_amount_range', { min, max })
      if (min) return helper.trans('plan_settings.plan_deal_amount_min') + ': ' + min
      if (max) return helper.trans('plan_settings.plan_deal_amount_max') + ': ' + max
      return null
    }

    const validatePlanSetting = (setting: {
      rewardType?: PlanRewardType | string;
      planRequisiteLimitDisplay?: string;
      rewardPriority?: PlanRewardPriority | string | null;
      planDealAmountMinDisplay?: string;
      planDealAmountMaxDisplay?: string;
    }): string | null => {
      if (setting.rewardType === PlanRewardType.UNLOCK_REQUISITES) {
        const limitNum = parseFloat((setting.planRequisiteLimitDisplay || '0').replace(',', '.'))
        if (Number.isNaN(limitNum) || limitNum <= 0) {
          return helper.trans('plan_settings.validation_limit_required_positive')
        }
      }
      if (setting.rewardType === PlanRewardType.PRIORITY_BOOST) {
        if (setting.rewardPriority == null || setting.rewardPriority === undefined || setting.rewardPriority === '') {
          return helper.trans('plan_settings.validation_reward_priority_required')
        }
      }
      const minStr = normalizeDealAmount(setting.planDealAmountMinDisplay)
      const maxStr = normalizeDealAmount(setting.planDealAmountMaxDisplay)
      if (minStr && maxStr) {
        const minNum = parseFloat(minStr)
        const maxNum = parseFloat(maxStr)
        if (!Number.isNaN(minNum) && !Number.isNaN(maxNum) && minNum > maxNum) {
          return helper.trans('validation.min_sum_must_be_less_than_max')
        }
      }
      return null
    }

    const saveSetting = async () => {
      if (!editingSetting.value) return

      const validationError = validatePlanSetting(editingSetting.value)
      if (validationError) {
        notification.notify({ type: 'error', text: validationError })
        return
      }

      try {
        const timerInSeconds = convertTimeToSeconds(editingSetting.value.planTimerDisplay)
        const limitValue = editingSetting.value.rewardType === PlanRewardType.PRIORITY_BOOST ? '0' : (editingSetting.value.planRequisiteLimitDisplay || '0')
        const dealAmountMin = normalizeDealAmount(editingSetting.value.planDealAmountMinDisplay)
        const dealAmountMax = normalizeDealAmount(editingSetting.value.planDealAmountMaxDisplay)

        if (editingSetting.value.id && editingSetting.value.id > 0) {
          const updateData: TraderRequisitesSettingUpdateRequestDTO = {
            id: editingSetting.value.id,
            name: editingSetting.value.name || '',
            currency: editingSetting.value.currency || '',
            planRequisiteLimit: limitValue,
            planTimer: timerInSeconds,
            planCompletedDeal: editingSetting.value.planCompletedDeal || 0,
            allowedPaymentOptions: editingSetting.value.allowedPaymentOptions,
            planType: editingSetting.value.planType ?? PlanType.MANDATORY,
            rewardType: editingSetting.value.rewardType ?? PlanRewardType.UNLOCK_REQUISITES,
            rewardPriority: editingSetting.value.rewardType === PlanRewardType.PRIORITY_BOOST ? (editingSetting.value.rewardPriority ?? null) : null
          }
          if (dealAmountMin !== '') updateData.planDealAmountMin = dealAmountMin
          if (dealAmountMax !== '') updateData.planDealAmountMax = dealAmountMax
          await api.updateTraderRequisitesSettings(updateData)
        } else {
          const createData: TraderRequisitesSettingCreateRequestDTO = {
            name: editingSetting.value.name || '',
            currency: editingSetting.value.currency || '',
            planRequisiteLimit: limitValue,
            planTimer: timerInSeconds,
            planCompletedDeal: editingSetting.value.planCompletedDeal || 0,
            allowedPaymentOptions: editingSetting.value.allowedPaymentOptions,
            planType: editingSetting.value.planType ?? PlanType.MANDATORY,
            rewardType: editingSetting.value.rewardType ?? PlanRewardType.UNLOCK_REQUISITES,
            rewardPriority: editingSetting.value.rewardType === PlanRewardType.PRIORITY_BOOST ? (editingSetting.value.rewardPriority ?? null) : null
          }
          if (dealAmountMin !== '') createData.planDealAmountMin = dealAmountMin
          if (dealAmountMax !== '') createData.planDealAmountMax = dealAmountMax
          await api.createTraderRequisitesSettings(createData)
        }

        notification.notify({
          type: 'success',
          text: helper.trans('labels.success')
        })

        await loadSettings()
        editingSetting.value = null
      } catch (error) {
        notification.notify({
          type: 'error',
          text: helper.trans('server_errors.INTERNAL_ERROR')
        })
      }
    }

    const cancelEditing = () => {
      editingSetting.value = null
    }

    const onCardRewardTypeChange = (setting: any) => {
      if (setting.rewardType === PlanRewardType.PRIORITY_BOOST) {
        setting.planRequisiteLimitDisplay = '0.00'
        if (setting.rewardPriority == null || setting.rewardPriority === undefined) {
          setting.rewardPriority = PlanRewardPriority.MEDIUM
        }
      }
    }

    const onEditingRewardTypeChange = () => {
      if (!editingSetting.value) return
      if (editingSetting.value.rewardType === PlanRewardType.PRIORITY_BOOST) {
        editingSetting.value.planRequisiteLimitDisplay = '0.00'
        if (editingSetting.value.rewardPriority == null || editingSetting.value.rewardPriority === undefined) {
          editingSetting.value.rewardPriority = PlanRewardPriority.MEDIUM
        }
      }
    }

    const updateSetting = async (setting: any) => {
      const validationError = validatePlanSetting(setting)
      if (validationError) {
        notification.notify({ type: 'error', text: validationError })
        return
      }

      try {
        const timerInSeconds = convertTimeToSeconds(setting.planTimerDisplay)
        const limitValue = setting.rewardType === PlanRewardType.PRIORITY_BOOST ? '0' : (setting.planRequisiteLimitDisplay || '0')
        
        const dealAmountMin = normalizeDealAmount(setting.planDealAmountMinDisplay)
        const dealAmountMax = normalizeDealAmount(setting.planDealAmountMaxDisplay)
        const updateData: TraderRequisitesSettingUpdateRequestDTO = {
          id: setting.id,
          name: setting.name || '',
          currency: setting.currency || '',
          planRequisiteLimit: limitValue,
          planTimer: timerInSeconds,
          planCompletedDeal: setting.planCompletedDeal || 0,
          allowedPaymentOptions: setting.allowedPaymentOptions,
          planType: setting.planType ?? PlanType.MANDATORY,
          rewardType: setting.rewardType ?? PlanRewardType.UNLOCK_REQUISITES,
          rewardPriority: setting.rewardType === PlanRewardType.PRIORITY_BOOST ? (setting.rewardPriority ?? null) : null
        }
        if (dealAmountMin !== '') updateData.planDealAmountMin = dealAmountMin
        if (dealAmountMax !== '') updateData.planDealAmountMax = dealAmountMax
        await api.updateTraderRequisitesSettings(updateData)

        notification.notify({
          type: 'success',
          text: helper.trans('labels.success')
        })

        await loadSettings()
      } catch (error) {
        notification.notify({
          type: 'error',
          text: helper.trans('server_errors.INTERNAL_ERROR')
        })
      }
    }

    const deleteSetting = async (setting: any) => {
      try {
        notification.notify({
          type: 'info',
          text: helper.trans('plan_settings.delete_not_implemented')
        })
      } catch (error) {
        notification.notify({
          type: 'error',
          text: helper.trans('server_errors.INTERNAL_ERROR')
        })
      }
    }

    const disablePlanForAll = async () => {
      try {
        const toggleData: TraderRequisitesSettingToggleRequestDTO = {
          disableAll: true
        }
        await api.toggleAllTraderLimits(toggleData)

        notification.notify({
          type: 'success',
          text: helper.trans('plan_settings.plan_disabled_for_all_traders')
        })
      } catch (error) {
        notification.notify({
          type: 'error',
          text: helper.trans('server_errors.INTERNAL_ERROR')
        })
      }
    }

    const enablePlanForSetting = async (settingId: number) => {
      try {
        const toggleData: TraderRequisitesSettingToggleRequestDTO = {
          enableAll: true,
          settingId: settingId
        }
        await api.toggleAllTraderLimits(toggleData)

        notification.notify({
          type: 'success',
          text: helper.trans('plan_settings.plan_enabled_for_all_traders')
        })
      } catch (error) {
        notification.notify({
          type: 'error',
          text: helper.trans('server_errors.INTERNAL_ERROR')
        })
      }
    }

    const disablePlanForSetting = async (settingId: number) => {
      try {
        const toggleData: TraderRequisitesSettingToggleRequestDTO = {
          disableAll: true,
          settingId: settingId
        }
        await api.toggleAllTraderLimits(toggleData)

        notification.notify({
          type: 'success',
          text: helper.trans('plan_settings.plan_disabled_for_all_traders')
        })
      } catch (error) {
        notification.notify({
          type: 'error',
          text: helper.trans('server_errors.INTERNAL_ERROR')
        })
      }
    }



    const openModal = () => {
      show.value = true
      loadSettings()
    }

    watch(() => props.show, (newValue) => {
      if (newValue) {
        loadSettings()
      }
    })

    return {
      loading,
      settings,
      form,
      helper,
      show,
      openModal,
      currencyList,
      paymentOptionsList,
      editingSetting,
      addNewSetting,
      editSetting,
      saveSetting,
      cancelEditing,
      getDealAmountRangeText,
      disablePlanForAll,
      enablePlanForSetting,
      disablePlanForSetting,
      updateSetting,
      deleteSetting,
      onCardRewardTypeChange,
      onEditingRewardTypeChange,
      PlanType,
      PlanRewardType,
      PlanRewardPriority
    }
  }
})
</script>
